import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get_order_detail, change_dispute_status, get_dispute_detail } from "../../reduxData/user/userAction";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";
import DisputeTimer from "../DisputeManage/DisputeTimer";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import TranslatedText from "../../hooks/TranslatedText";
import {
    DISPUTE_STATUS_LIST,
    FINAL_DISPUTE_STATUSES,
    disputeStatusLabel,
    disputeTagClass,
    normalizeDisputeStatus,
} from "./disputeStatus";


const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;

const getFullImageUrl = (url) => {
    return url.startsWith("http") || url.startsWith("https")
        ? url
        : `${REACT_APP_IMAGE_URL}${url}`;
};

const DisputeDetail = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();    
    const location = useLocation();    
    const { id: dispute_id } = useParams();
    const { userPermissions } = useSelector((state) => state.auth);
    const { disputeDetail, orderDetail } = useSelector((state) => state.user);
    const disputeOrderId = location?.state?.data?.order_id || null;
    const currentOrderDetail = orderDetail?.[0] ?? null;
    const disputeData = disputeDetail?.[0] || disputeDetail || null;
    const disputedItems = disputeData?.disputed_items || disputeData?.items || [];
    const [selectedStatus, setSelectedStatus] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [reason, setReason] = useState("");
    const [showReasonError, setShowReasonError] = useState(false);

    const statusOptions = DISPUTE_STATUS_LIST.map((value) => ({
        value,
        label: disputeStatusLabel(value, t),
    }));

    const currentStatus = normalizeDisputeStatus(disputeData?.status);
    const isFinalized = FINAL_DISPUTE_STATUSES.includes(currentStatus);
    const isFinalSelection = FINAL_DISPUTE_STATUSES.includes(selectedStatus);
    const hasChanges = !!selectedStatus && selectedStatus !== currentStatus;

    // Keep the select's value in sync once dispute data loads/refreshes
    useEffect(() => {
        setSelectedStatus(normalizeDisputeStatus(disputeData?.status));
    }, [disputeData?.status]);

    useEffect(() => {
        if (!disputeOrderId || !dispute_id) return;
        const fetchData = async () => {
            await get_dispute_detail(dispatch, disputeOrderId, dispute_id)
            await get_order_detail(dispatch, disputeOrderId);
        };
        fetchData();
    }, [dispatch, disputeOrderId]);

    const handleDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    const handleContactPhone = () => {
        const seller = currentOrderDetail?.sellerdata;
        if (seller?.phone_no) {
            window.location.href = `tel:${seller.country_code}${seller.phone_no}`;
        }
    };

    // ----- Admin status change / confirmation logic -----
    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setShowError(false);
    };

    const handleUpdate = () => {
        if (!selectedStatus || selectedStatus === currentStatus) {
            setShowError(true);
            return;
        }
        setReason("");
        setShowReasonError(false);
        setShowConfirmModal(true);
    };

    const closeConfirmModal = () => {
        if (isUpdating) return;
        setShowConfirmModal(false);
        setReason("");
        setShowReasonError(false);
    };

    const saveStatusUpdate = async () => {
        const trimmedReason = reason.trim();
        if (!trimmedReason) {
            setShowReasonError(true);
            return;
        }
        setIsUpdating(true);
        try {
            const payload = {
                dispute_id: dispute_id,
                order_id: disputeOrderId,
                status: selectedStatus,
                reason: trimmedReason,
            };
            const res = await change_dispute_status(payload, dispatch);
            if (res?.data?.status) {
                await get_dispute_detail(dispatch, disputeOrderId, dispute_id);
                setShowConfirmModal(false);
                setReason("");
            }
        } catch (err) {
            console.error("Failed to update dispute status:", err);
        } finally {
            setIsUpdating(false);
        }
    };

    const calculateDisputedTotal = () => {
        if (!disputedItems || disputedItems.length === 0) return 0;

        return disputedItems.reduce((total, item) => {
            const price = parseFloat(
                item?.original_retail_price ||
                item?.itemData?.original_retail_price ||
                0
            );
            const quantity = parseInt(item?.itemData?.quantity || item?.quantity || 1, 10);
            return total + (price * quantity);
        }, 0);
    };

    const disputedTotal = calculateDisputedTotal();

    return (
        <div className="od2-page container">
            <div className="od2-header">
                <div className="od2-header-left">
                    <div>
                        <h2 className="od2-order-title">{t("disputeDetails.orderDispute")}</h2>
                        <div className="od2-order-id-label">
                            {t("disputeDetails.OrderId")}:
                            <span className="od2-order-id-number">
                                #{disputeData?.order_id || disputeOrderId || "-"}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="od2-header-actions">
                    <span className={`od2-status-pill ${disputeTagClass(disputeData?.status)}`}>
                        {disputeStatusLabel(disputeData?.status, t)}
                    </span>
                </div>
            </div>

            <div className="od2-layout">
                {/* Left Column - Disputed Items */}
                <div className="od2-col-left">
                    <div className="od2-card">
                        <div className="od2-card-header">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                {t("disputeDetails.DisputedItems") || "Disputed Items"}
                            </h4>
                        </div>

                        <div className="od2-card-body">
                            {disputedItems.length ? (
                                disputedItems.map((item, index) => {
                                    const title = item?.itemData?.title || "-";
                                    const imageUrl = getFullImageUrl(
                                        item?.variantData?.images?.[0] || item?.itemData?.ai_images?.[0]
                                    );
                                    const isLast = index === disputedItems.length - 1;
                                    const qty = item?.itemData?.quantity || 1;

                                    return (
                                        <div
                                            key={item?.id || index}
                                            className={`od2-item-row ${!isLast ? "od2-item-row--divider" : ""}`}
                                        >
                                            <img className="od2-item-img" src={imageUrl} alt={title} />

                                            <div className="od2-item-body">
                                                {item?.itemData?.brand_name && (
                                                    <p className="od2-item-brand">{item.itemData.brand_name}</p>
                                                )}

                                                <h5 className="od2-item-name">{title}</h5>

                                                <div className="od2-item-attrs">
                                                    {item?.variantData?.size_name && (
                                                        <span className="od2-attr-chip">
                                                            {t("disputeDetails.Size")}: <strong>{item.variantData.size_name}</strong>
                                                        </span>
                                                    )}
                                                    {item?.variantData?.color_name && (
                                                        <span className="od2-attr-chip">
                                                            {t("disputeDetails.Color")}:{" "}
                                                            <strong><TranslatedText text={item.variantData.color_name} />
                                                            </strong>
                                                        </span>
                                                    )}
                                                    {item?.itemData?.original_retail_price && (
                                                        <span className="od2-attr-chip od2-attr-chip--price">
                                                            {formatPrice(item.itemData.original_retail_price, i18n)}
                                                        </span>
                                                    )}
                                                    {qty && (
                                                        <span className="od2-attr-chip">
                                                            {t("disputeDetails.Quantity")}: <strong>{qty}</strong>
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="od2-actions">
                                                    <button className="od2-btn-detail" onClick={() => handleDetail(item.itemData?.slug)}>
                                                        {t("disputeDetails.MoreDetails")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="od2-summary-value">
                                    {t("disputeDetails.NoItemsInDispute")}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="od2-card">
                        <h4 className="od2-card-title mb-0">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
                            {t("disputeDetails.OrderSummary")}
                        </h4>
                        <div className="od2-summary-rows">
                            <div className="od2-summary-row">
                                <span className="od2-summary-label">{t("disputeDetails.Subtotal")}</span>
                                <span className="od2-summary-value">
                                    {formatPrice(disputedTotal, i18n)}
                                </span>
                            </div>
                            <div className="od2-summary-row total pb-0">
                                <span className="od2-summary-label">{t("disputeDetails.Total")}</span>
                                <span className="od2-summary-value">
                                    {formatPrice(disputedTotal, i18n)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="od2-col-right">
                    {currentOrderDetail?.sellerdata && (
                        <div className="od2-card">
                            <h4 className="od2-card-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                {t("disputeDetails.SellerDetails")}
                            </h4>
                            {currentOrderDetail.sellerdata.company_logo && (
                                <img
                                    className="od2-seller-logo"
                                    src={`${REACT_APP_IMAGE_URL}${currentOrderDetail.sellerdata.company_logo}`}
                                    alt="Seller logo"
                                />
                            )}
                            <p className="od2-seller-name">{currentOrderDetail.sellerdata.store_name}</p>
                            <ul className="od2-contact-list">
                                <li className="od2-contact-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                    {currentOrderDetail.sellerdata.user_name}
                                </li>
                                {currentOrderDetail.sellerdata.location && (
                                    <li className="od2-contact-item">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        {currentOrderDetail.sellerdata.location}
                                    </li>
                                )}
                                <li className="od2-contact-item">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                    <a href={`mailto:${currentOrderDetail.sellerdata.email}`} className="p-0">{currentOrderDetail.sellerdata.email}</a>
                                </li>
                                {currentOrderDetail.sellerdata.phone_no && (
                                    <li className="od2-contact-item clickable" onClick={handleContactPhone}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                        {currentOrderDetail.sellerdata.country_code} {currentOrderDetail.sellerdata.phone_no}
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div className="od2-card">
                        <h4 className="od2-card-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                            {t("disputeDetails.Information")}
                        </h4>
                        <div className="od2-info-list">
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                <span>{currentOrderDetail?.user_name}</span>
                            </div>
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                                <span>{currentOrderDetail?.user_email}</span>
                            </div>
                            <div className="od2-info-item">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                                <span>{currentOrderDetail?.user_country_code} {currentOrderDetail?.user_phone_no}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dispute Details Section (Merged) */}
            <div className="od2-card mt-4">
                <div className="od2-dispute-card">
                    <div className="od2-dispute-header">
                        <h3 className="od2-dispute-title">{t("disputeDetails.orderDispute")}</h3>
                        <span className={`od2-dispute-status-tag ${disputeTagClass(disputeData?.status)}`}>
                            {disputeStatusLabel(disputeData?.status, t)}
                        </span>
                    </div>

                    {disputeData?.comment && (
                        <>
                            <p className="od2-dispute-section-title">{t("disputeDetails.buyerAction")}</p>
                            <span className="od2-meta-item mb-2">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                {formatDateTime(disputeData?.buyer_created_at || disputeData?.created_at, i18n)}
                            </span>
                            <div className="od2-dispute-party">
                                <strong>{t("disputeDetails.name")}</strong> {disputeData?.buyer_name}<br />
                                <strong>{t("disputeDetails.email")}</strong> {disputeData?.buyer_email}
                            </div>
                            <div className="od2-return-section-label" style={{ marginBottom: 6 }}>{t("disputeDetails.disputeReason")}</div>
                            <p className="od2-dispute-reason">{disputeData?.comment}</p>
                            {disputeData.proofimages?.length > 0 && (
                                <>
                                    <div className="od2-return-section-label" style={{ marginBottom: 8 }}>{t("disputeDetails.uploadedEvidence")}</div>
                                    <div className="od2-evidence-grid">
                                        {disputeData.proofimages.map((img, i) => (
                                            <img
                                                key={i}
                                                className="od2-evidence-img"
                                                src={`${REACT_APP_IMAGE_URL}${img}`}
                                                alt="Evidence"
                                                onClick={() => window.open(`${REACT_APP_IMAGE_URL}${img}`, "_blank")}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                            {disputeData?.seller_comment && <div className="dispute-divide" />}
                        </>
                    )}

                    {disputeData?.seller_comment && (
                        <>
                            <p className="od2-dispute-section-title">{t("disputeDetails.sellerAction")}</p>
                            <span className="od2-meta-item mb-2">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                {formatDateTime(disputeData?.seller_created_at, i18n)}
                            </span>
                            <div className="od2-dispute-party">
                                <strong>{t("disputeDetails.name")}</strong> {disputeData?.seller_name}<br />
                                <strong>{t("disputeDetails.email")}</strong> {disputeData?.seller_email}
                            </div>
                            <div className="od2-return-section-label" style={{ marginBottom: 6 }}>{t("disputeDetails.disputeReason")}</div>
                            <p className="od2-dispute-reason">{disputeData?.seller_comment}</p>
                            {disputeData?.seller_proofimages?.length > 0 && (
                                <>
                                    <div className="od2-return-section-label" style={{ marginBottom: 8 }}>{t("disputeDetails.uploadedEvidence")}</div>
                                    <div className="od2-evidence-grid">
                                        {disputeData.seller_proofimages.map((img, i) => (
                                            <img
                                                key={i}
                                                className="od2-evidence-img"
                                                src={`${REACT_APP_IMAGE_URL}${img}`}
                                                alt="Evidence"
                                                onClick={() => window.open(`${REACT_APP_IMAGE_URL}${img}`, "_blank")}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {(disputeData?.seller_comment == null || disputeData?.comment == null) && (
                        <>
                            <div className="dispute-divide"></div>
                            <p className="od2-dispute-section-title">{disputeData?.comment == null ? t("disputeDetails.buyerAction") : t("disputeDetails.sellerAction")}</p>
                            {(() => {
                                const createdAt = disputeData?.seller_comment == null
                                    ? disputeData?.buyer_created_at
                                    : disputeData?.seller_created_at;
                                const daysPassed = createdAt
                                    ? (Date.now() - new Date(createdAt).getTime()) / 864e5
                                    : 0;
                                const within5Days = daysPassed <= 5;
                                return (
                                    <>
                                        <DisputeTimer created_at={createdAt} />
                                        {within5Days ? (
                                            <div className="od2-warning-notice">
                                                <p>{t("disputeDetails.responseMandatory")}</p>
                                            </div>
                                        ) : (
                                            <div className="od2-danger-notice">
                                                <p>{t("disputeDetails.deadlineMessage")}</p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    )}

                    <div className="divide" />

                    <div className="od2-dispute-admin">
                        {isFinalized ? (
                            <>
                                <p className="od2-dispute-section-title">{t("disputeDetails.adminAction")}</p>
                                <span className="od2-meta-item mb-2">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                    {formatDateTime(disputeData?.updated_at, i18n)}
                                </span>
                                <div className="od2-dispute-party">
                                    <strong>{t("disputeDetails.adminDecision")}:</strong>{" "}
                                    <span className={`od2-dispute-status-tag ${disputeTagClass(disputeData?.status)}`}>
                                        {disputeStatusLabel(disputeData?.status, t)}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="od2-dispute-admin-header">
                                    <div>
                                        <p className="od2-dispute-section-title">{t("disputeDetails.adminAction")}</p>
                                        <div className="od2-dispute-current-status">
                                            <span className="od2-dispute-current-label">{t("disputeDetails.currentStatus")}</span>
                                            <span className={`od2-dispute-status-tag ${disputeTagClass(disputeData?.status)}`}>
                                                {disputeStatusLabel(disputeData?.status, t)}
                                            </span>
                                        </div>
                                    </div>
                                    {userPermissions?.disputes?.edit ? (
                                        <button
                                            onClick={handleUpdate}
                                            disabled={!hasChanges || isUpdating}
                                            className="od2-update-status-btn"
                                        >
                                            {isUpdating ? t("disputeDetails.updating") : t("disputeDetails.saveChanges")}
                                        </button>
                                    ) : (
                                        <p className="od2-dispute-note">{t("disputeDetails.noPermission")}</p>
                                    )}
                                </div>

                                <div className="od2-dispute-admin-controls">
                                    <label htmlFor="dispute-status-select" className="od2-dispute-select-label">
                                        {t("disputeDetails.selectNewStatus")}
                                    </label>
                                    <select
                                        id="dispute-status-select"
                                        value={selectedStatus || ''}
                                        onChange={handleStatusChange}
                                        className="od2-dispute-select"
                                    >
                                        {statusOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {userPermissions?.disputes?.edit && !hasChanges && (
                                        <p className="od2-dispute-hint">{t("disputeDetails.selectDifferentOption")}</p>
                                    )}
                                </div>

                                {showError && (
                                    <p className="od2-dispute-error">{t("disputeDetails.selectValidStatus")}</p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Status update modal: asks the admin for a reason before saving */}
            <Modal open={showConfirmModal} onClose={closeConfirmModal}>
                <ModalDialog className="modern-modal small-modal">
                    <div className="modal-header-custom">
                        <div>
                            <h3>{isFinalSelection ? t("disputeDetails.confirmTitle") : t("disputeDetails.updateStatusTitle")}</h3>
                        </div>
                        <button
                            className="close-btn"
                            onClick={closeConfirmModal}
                            disabled={isUpdating}
                        >
                            ✕
                        </button>
                    </div>

                    <div className="modal-body-custom">
                        {isFinalSelection && <p>{t("disputeDetails.confirmText")}</p>}
                        <div className="od2-dispute-party" style={{ marginTop: isFinalSelection ? 12 : 0 }}>
                            <strong>{t("disputeDetails.newStatus")}:</strong>{" "}
                            <span className={`od2-dispute-status-tag ${disputeTagClass(selectedStatus)}`}>
                                {disputeStatusLabel(selectedStatus, t)}
                            </span>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <label htmlFor="dispute-status-reason" className="od2-dispute-select-label">
                                {t("disputeDetails.statusChangeReason")} *
                            </label>
                            <textarea
                                id="dispute-status-reason"
                                className="od2-dispute-select"
                                style={{ height: "auto", minHeight: 96, resize: "vertical" }}
                                rows={4}
                                value={reason}
                                onChange={(e) => {
                                    setReason(e.target.value);
                                    setShowReasonError(false);
                                }}
                                placeholder={t("disputeDetails.statusChangeReasonPlaceholder")}
                                disabled={isUpdating}
                            />
                            {showReasonError && (
                                <p className="od2-dispute-error">{t("disputeDetails.statusChangeReasonRequired")}</p>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="secondary-btn"
                            onClick={closeConfirmModal}
                            disabled={isUpdating}
                        >
                            {t("disputeDetails.No")}
                        </button>
                        <button
                            type="button"
                            className="primary-btn"
                            onClick={saveStatusUpdate}
                            disabled={isUpdating}
                        >
                            {isUpdating ? (t("disputeDetails.updating")) : (t("disputeDetails.Yes"))}
                        </button>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default DisputeDetail;
